import React, { useState, useEffect, useContext } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { isEqual, pull, uniq } from 'lodash';
import queryString from 'query-string';
import { useMediaQuery, createBreakpoints } from 'WeployDesignLanguage/mediaQueries';

import Grid from 'LegacyWeployDesignLanguage/Grid';
import LeftArrowIconButton from 'LegacyWeployDesignLanguage/icons/LeftArrowIconButton';
import Loader from 'LegacyWeployDesignLanguage/loaders/Loader';
import Select from 'LegacyWeployDesignLanguage/inputs/Select';
import Snackbar from 'LegacyWeployDesignLanguage/Snackbar';
import { Tabs, Tab } from 'LegacyWeployDesignLanguage/Tabs';

import PaymentSummary from './PaymentSummary';
import SubmittedTimesheets from '../../../components/weployer/timesheets/SubmittedTimesheets';
import ApprovedTimesheets from '../../../components/weployer/timesheets/ApprovedTimesheets';
import RejectedTimesheets from '../../../components/weployer/timesheets/RejectedTimesheets';
import EmptyTimesheets from '../../../components/weployer/timesheets/EmptyTimesheets';
import ApprovalSuccess from '../../../components/weployer/timesheets/ApprovalSuccess';
import TimesheetSummaryModal from '../../../components/weployer/timesheets/TimesheetSummaryModal';

import {
  APPROVE_TIMESHEETS,
  GET_LOCATION_COUNTRIES,
} from './graphql/queries';
import {
  orderedTimesheets,
  mapOpenTimesheet,
  mapTimesheetsToDownload,
} from './mappers';
import {
  ME,
  ORGANISATION,
  OWNED_BY_FILTER_KEY,
} from '../common/graphql/constants';
import {
  useTimesheetQuery,
  useSubmittedTimesheetsQuery,
  useApprovedTimesheetsQuery,
} from './hooks/api';
import {
  trackPageView,
  trackEvent,
  EXPORT_TIMESHEETS_BUTTON_CLICK,
  EXPORT_TIMESHEETS_SELECT_ALL_CLICK,
  TIMESHEETS,
  TIMESHEETS_APPROVE_BUTTON_CLICK,
  TIMESHEETS_BACK_BUTTON_CLICK,
  TIMESHEETS_BACK_TO_TIMESHEETS_BUTTON_CLICK,
  TIMESHEETS_BILLING_DETAILS_CLICK,
  TIMESHEETS_CONFIRM_BUTTON_CLICK,
  TIMESHEETS_COUPON_SELECT,
  TIMESHEETS_DEEP_LINK_OPEN,
  TIMESHEETS_PAYMENT_TERM_SELECT,
  TIMESHEETS_QUERY_EMAIL_CLICK,
  TIMESHEETS_SELECT_ALL_CLICK,
  TIMESHEETS_SKILL_INVOICE_OPEN,
  TIMESHEETS_SUMMARY_MODAL_OPEN,
  TIMESHEETS_SUBMITTED_TAB_CLICK,
  TIMESHEETS_APPROVED_TAB_CLICK,
  WEPLOYER,
} from '../../../utils/analytics';
import { PolicyContext, APPROVE_ORGANISATION_TIMESHEET } from '../PolicyProvider';
import { useScrollReset } from '../common/hooks';
import {
  getInternalPath,
  TIMESHEETS_PATH,
  TIMESHEETS_NO_PARAMS_PATH,
} from '../pathHelpers';

const { Option } = Select;

const SELECTION_PAGE = 0;
const PAYMENT_SUMMARY_PAGE = 1;
const APPROVAL_SUCCESS_PAGE = 2;

const APPROVAL_SUCCESS_LOADING_TIMER = 3000;

const COUNTRIES = {
  NZ: 'New Zealand',
  AU: 'Australia',
};

const OWNED_BY_FILTERS = {
  [ME]: 'Owned by me',
  [ORGANISATION]: 'Owned by Organisation',
};

const SUBMITTED_TIMESHEETS_TAB = 'Submitted';
const APPROVED_TIMESHEETS_TAB = 'Approved';
const REJECTED_TIMESHEETS_TAB = 'Rejected';

const TAB_NAMES = [
  SUBMITTED_TIMESHEETS_TAB,
  APPROVED_TIMESHEETS_TAB,
  REJECTED_TIMESHEETS_TAB,
];

const StyledPaymentSummaryHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTitle = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StyledHeader = styled.h5`
  padding-top: ${props => props.theme.spacing[1]};
`;

const StyledSubHeading = styled.p`
  margin-left: ${props => props.theme.spacing[6]};

  ${props => props.mobile && css`
    margin-left: ${props.theme.spacing[0]};
  `};
`;

const StyledSubtitle = styled.p`
  ${props => props.theme.typography.body1}
  color: ${props => props.theme.colors.text.primary};
  margin-top: ${props => props.theme.spacing[0]};
`;

const StyledLargeWrapper = styled.div`
  margin: 0 auto;
  @media only screen and (min-width: 1224px) {
    width: 1200px;
  }
`;

const StyledMediumWrapper = styled.div`
  margin: 0 auto;
  @media only screen and (min-width: 960px) {
    max-width: 800px;
  }
  @media only screen and (min-width: 1280px) {
    max-width: 1200px;
  }
`;

const StyledSmallWrapper = styled.div`
  margin: 0 auto;
  @media only screen and (min-width: 1224px) {
    width: 600px;
  }
`;

const StyledOwnedByFilter = styled.div`
  padding-top: ${props => props.theme.spacing[1]};
`;

const Timesheets = ({
  applicationConfig: { features: { FEATURE_FLAG_REJECTING_TIMESHEET } },
  onWeployerDetailsChange,
  weployerDetails,
  match: { params },
  location: { search: queryParams },
}) => {
  const parsedOriginPath = queryString.parse(queryParams);
  const nullCoupon = { remainingCredit: 0, id: '', code: '' };
  const [currentPage, setCurrentPage] = useState(SELECTION_PAGE);
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState({ chargePercentage: 0 });
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState([]);
  const [selectedTimesheetIdsToDownload, setSelectedTimesheetIdsToDownload] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [approvalSuccessLoading, setApprovalSuccessLoading] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(nullCoupon);
  const [selectedTimesheetCountry, setSelectedTimesheetCountry] = useState(null);

  const [ownedByFilter, setOwnedByFilter] = useState(weployerDetails[OWNED_BY_FILTER_KEY]);
  const [refetchingFromFilter, setRefetchingFromFilter] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openTimesheet, setOpenTimesheet] = useState(null);
  const [mobileSelectMode, setMobileSelectMode] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [downloadReady, setDownloadReady] = useState(false);

  const { DOWN_SM } = createBreakpoints();
  const mobileUser = useMediaQuery(DOWN_SM);

  const { actions } = useContext(PolicyContext);
  const history = useHistory();

  const { timesheetId: timesheetPathParam } = params;

  const onTimesheetCountriesCompleted = (data) => {
    const countries = data.weployer.locationCountries;

    setSelectedTimesheetCountry(countries[0]);
  };

  const { data: timesheetCountries, loading: timesheetCountriesLoading } = useQuery(GET_LOCATION_COUNTRIES, {
    onCompleted: onTimesheetCountriesCompleted,
  });

  const {
    totalTimesheetsCount: totalSubmittedTimesheetsCount,
    timesheets: timesheetsAwaitingApproval,
    orderedTimesheetIds: orderedTimesheetsAwaitingApprovalIds,
    fetch: fetchSubmittedTimesheets,
    fetchMore: fetchMoreSubmittedTimesheets,
    loading: submittedTimesheetsLoading,
    hasMore: hasMoreSubmittedTimesheets,
    handleRejectTimesheetSuccess,
  } = useSubmittedTimesheetsQuery({ ownedBy: ownedByFilter, country: selectedTimesheetCountry });
  const {
    totalTimesheetsCount: totalApprovedTimesheetsCount,
    timesheets: approvedTimesheets,
    orderedTimesheetIds: orderedApprovedTimesheetIds,
    fetch: fetchApprovedTimesheets,
    fetchMore: fetchMoreApprovedTimesheets,
    loading: approvedTimesheetsLoading,
    hasMore: hasMoreApprovedTimesheets,
  } = useApprovedTimesheetsQuery({ ownedBy: ownedByFilter });
  const {
    deeplinkedTimesheet,
    fetchTimesheet,
  } = useTimesheetQuery({ ownedBy: ownedByFilter });

  const timesheetAlreadyFetched = id => (
    orderedTimesheetsAwaitingApprovalIds.includes(id) || orderedApprovedTimesheetIds.includes(id)
  );

  useEffect(() => {
    trackPageView(WEPLOYER, TIMESHEETS, { ...parsedOriginPath });

    if (timesheetPathParam && !timesheetAlreadyFetched(timesheetPathParam)) {
      fetchTimesheet(timesheetPathParam);
    }
  }, []);

  const [approveTimesheets] = useMutation(APPROVE_TIMESHEETS);

  const handlePaymentTermSelect = (paymentTermId, paymentTerms) => {
    trackEvent(TIMESHEETS_PAYMENT_TERM_SELECT);
    setSelectedPaymentTerm(paymentTerms.find(paymentTerm => (paymentTerm.id === paymentTermId)));
  };

  const handleApprovalSuccessLoading = () => {
    setApprovalSuccessLoading(true);
    setTimeout(() => {
      setApprovalSuccessLoading(false);
    }, APPROVAL_SUCCESS_LOADING_TIMER);
  };

  const handleCouponSelect = (id, coupons) => {
    trackEvent(TIMESHEETS_COUPON_SELECT);
    const findCoupon = coupons.find(coupon => (coupon.id === id)) || nullCoupon;
    setSelectedCoupon(findCoupon);
  };

  const handleConfirmButtonClick = () => {
    trackEvent(TIMESHEETS_CONFIRM_BUTTON_CLICK, { numberOfTimesheetsConfirmed: uniq(selectedTimesheetIds).length });
    approveTimesheets({ variables: { timesheetIds: selectedTimesheetIds, paymentTermId: selectedPaymentTerm.id, couponCode: selectedCoupon.code } });
    handleApprovalSuccessLoading();
    setCurrentPage(APPROVAL_SUCCESS_PAGE);
  };

  const handleTabClick = (_, index) => {
    setTabIndex(index);
    setSelectedTimesheetIds([]);
    setSelectedTimesheetIdsToDownload([]);

    switch (TAB_NAMES[index]) {
      case REJECTED_TIMESHEETS_TAB:
        break;
      case APPROVED_TIMESHEETS_TAB:
        trackEvent(TIMESHEETS_APPROVED_TAB_CLICK);
        break;
      case SUBMITTED_TIMESHEETS_TAB:
      default:
        trackEvent(TIMESHEETS_SUBMITTED_TAB_CLICK);
    }
  };

  useScrollReset(currentPage);

  useEffect(() => {
    switch (TAB_NAMES[tabIndex]) {
      case APPROVED_TIMESHEETS_TAB: {
        if (orderedApprovedTimesheetIds.length) { break; }

        fetchApprovedTimesheets();
        break;
      }
      case SUBMITTED_TIMESHEETS_TAB:
      default: {
        if (orderedTimesheetsAwaitingApprovalIds.length) { break; }

        fetchSubmittedTimesheets();
        break;
      }
    }
  }, [tabIndex, selectedTimesheetCountry]);

  useEffect(() => {
    fetchApprovedTimesheets();
    fetchSubmittedTimesheets();
  }, [ownedByFilter]);

  useEffect(() => {
    const { [OWNED_BY_FILTER_KEY]: ownedByFilterFromLocalStorage } = weployerDetails;

    if (ownedByFilterFromLocalStorage) {
      setOwnedByFilter(ownedByFilterFromLocalStorage);
    }
  }, [weployerDetails]);

  const handleTimesheetCountryChanged = (event) => {
    const { value } = event.target;

    setSelectedTimesheetCountry(value);
  };

  const handleOwnedByChange = (event) => {
    const { value } = event.target;

    if (ownedByFilter !== value) {
      setRefetchingFromFilter(true);
    }

    setOwnedByFilter(value);
    setSelectedTimesheetIds([]);
    setSelectedTimesheetIdsToDownload([]);
    onWeployerDetailsChange(OWNED_BY_FILTER_KEY, value);
  };

  useEffect(() => {
    const loadingInSomeWay = submittedTimesheetsLoading || approvedTimesheetsLoading;

    if (refetchingFromFilter && !loadingInSomeWay) {
      setRefetchingFromFilter(false);
    }
  }, [submittedTimesheetsLoading, approvedTimesheetsLoading]);

  useEffect(() => {
    if (deeplinkedTimesheet) {
      trackEvent(TIMESHEETS_DEEP_LINK_OPEN, { deeplinkedTimesheetId: deeplinkedTimesheet.id });
      setOpenTimesheet(mapOpenTimesheet(deeplinkedTimesheet));
      setModalOpen(true);
    }
  }, [deeplinkedTimesheet]);

  const handleTimesheetSelectChange = (timesheetId) => {
    const newSelectedTimesheetIds = [...selectedTimesheetIds];

    if (newSelectedTimesheetIds.includes(timesheetId)) {
      pull(newSelectedTimesheetIds, timesheetId);
    } else {
      newSelectedTimesheetIds.push(timesheetId);
    }
    setSelectedTimesheetIds(newSelectedTimesheetIds);
  };

  const handleSelectedTimesheetsToDownloadChange = (timesheetId) => {
    setDownloadReady(false);
    const newSelectedTimesheetIdsToDownload = [...selectedTimesheetIdsToDownload];

    if (newSelectedTimesheetIdsToDownload.includes(timesheetId)) {
      pull(newSelectedTimesheetIdsToDownload, timesheetId);
    } else {
      newSelectedTimesheetIdsToDownload.push(timesheetId);
    }
    setSelectedTimesheetIdsToDownload(newSelectedTimesheetIdsToDownload);
  };

  const handleTimesheetSelectAll = () => {
    trackEvent(TIMESHEETS_SELECT_ALL_CLICK);
    let newSelectedTimesheetIds = [...selectedTimesheetIds];

    if (isEqual(newSelectedTimesheetIds, orderedTimesheetsAwaitingApprovalIds)) {
      newSelectedTimesheetIds = [];
    } else {
      newSelectedTimesheetIds = orderedTimesheetsAwaitingApprovalIds;
      if (!mobileSelectMode) setMobileSelectMode(true);
    }
    setSelectedTimesheetIds(newSelectedTimesheetIds);
  };

  const handleApprovedTimesheetSelectAll = () => {
    setDownloadReady(false);
    trackEvent(EXPORT_TIMESHEETS_SELECT_ALL_CLICK);
    let newSelectedTimesheetIds = [...selectedTimesheetIdsToDownload];

    if (isEqual(newSelectedTimesheetIds, orderedApprovedTimesheetIds)) {
      newSelectedTimesheetIds = [];
    } else {
      newSelectedTimesheetIds = orderedApprovedTimesheetIds;
    }
    setSelectedTimesheetIdsToDownload(newSelectedTimesheetIds);
  };

  const timesheetsForActiveTab = () => {
    switch (TAB_NAMES[tabIndex]) {
      case APPROVED_TIMESHEETS_TAB:
        return approvedTimesheets;
      case SUBMITTED_TIMESHEETS_TAB:
      default:
        return timesheetsAwaitingApproval;
    }
  };

  const handleTimesheetRowClick = (timesheetId) => {
    trackEvent(TIMESHEETS_SUMMARY_MODAL_OPEN);
    const timesheets = timesheetsForActiveTab();
    setOpenTimesheet(mapOpenTimesheet(timesheets[timesheetId]));
    setModalOpen(true);
    history.push(getInternalPath({
      pathName: TIMESHEETS_PATH,
      // Ideally this pathParam should not have a question mark
      // TODO: after pathHelper is refactored we also name this properly
      // https://app.asana.com/0/887543564047500/1166134324701473/f
      pathParams: { 'timesheetId?': timesheetId },
      params: { origin_path: '%2Fnav%2Ftimesheets' },
    }));
  };

  const handleApproveButtonClick = () => {
    trackEvent(TIMESHEETS_APPROVE_BUTTON_CLICK);
    setCurrentPage(PAYMENT_SUMMARY_PAGE);
  };

  const handleMobileSelectModeToggle = () => {
    if (selectedTimesheetIds.length > 0) setSelectedTimesheetIds([]);
    setMobileSelectMode(!mobileSelectMode);
  };

  useEffect(() => {
    if (selectedTimesheetIdsToDownload.length) {
      setDownloadReady(true);
    } else if (downloadReady) {
      setDownloadReady(false);
    }
  }, [selectedTimesheetIdsToDownload]);

  const handleRejectingTimesheet = ({ timesheetId, rejectionReason }) => {
    handleRejectTimesheetSuccess({ timesheetId, rejectionReason });
  };

  const renderTimesheetCountries = () => (
    <StyledOwnedByFilter>
      <Grid container justify="space-between">
        <Grid item>
          <Loader loading={timesheetCountriesLoading} text="Loading...">
            { timesheetCountries && timesheetCountries.weployer.locationCountries.length > 1 &&
              <Select
                type={Select.TEXT}
                value={COUNTRIES[selectedTimesheetCountry]}
                onChange={handleTimesheetCountryChanged}
                name="timesheetCountry"
                id="timesheet-country"
              >
                { timesheetCountries.weployer.locationCountries.map(country => <Option key={`timesheet-country-${country}`} value={country}>{COUNTRIES[country]}</Option>) }
              </Select>
            }
          </Loader>
        </Grid>
      </Grid>
    </StyledOwnedByFilter>
  );

  const renderSubmittedTimesheets = () => {
    const submittedTimesheets = orderedTimesheets(timesheetsAwaitingApproval, orderedTimesheetsAwaitingApprovalIds);

    return (
      <>
        { renderTimesheetCountries() }
        <Loader loading={timesheetCountriesLoading || (submittedTimesheetsLoading && !submittedTimesheets.length)} text="Loading...">
          { submittedTimesheets.length === 0
            ? (
              <EmptyTimesheets
                title="No timesheets to approve"
                description="Weployees&apos; timesheets to approve will show up here"
              />
            )
            : (
              <SubmittedTimesheets
                timesheets={submittedTimesheets}
                totalTimesheetsCount={totalSubmittedTimesheetsCount}
                onTimesheetRowClick={handleTimesheetRowClick}
                onTimesheetSelectChange={handleTimesheetSelectChange}
                getChecked={id => selectedTimesheetIds.includes(id)}
                mobileUser={mobileUser}
                mobileSelectMode={mobileSelectMode}
                onBottomReached={fetchMoreSubmittedTimesheets}
                isEndReached={!hasMoreSubmittedTimesheets}
                scrollHeight={80}
                onTimesheetSelectAll={handleTimesheetSelectAll}
                areAllTimesheetsSelected={selectedTimesheetIds.length > 0 && isEqual(selectedTimesheetIds, orderedTimesheetsAwaitingApprovalIds)}
                onMobileSelectModeToggle={handleMobileSelectModeToggle}
                selectedTimesheetsCount={selectedTimesheetIds.length}
                onApproveButtonClick={handleApproveButtonClick}
              />
            )
          }
        </Loader>
      </>
    );
  };

  const renderApprovedTimesheets = () => {
    const mappedApprovedTimesheets = orderedTimesheets(approvedTimesheets, orderedApprovedTimesheetIds);

    return (
      <Loader loading={approvedTimesheetsLoading && !mappedApprovedTimesheets.length} text="Loading...">
        { mappedApprovedTimesheets.length === 0
          ? (
            <EmptyTimesheets
              title="No approved timesheets"
              description="Weployees&apos; approved timesheets will show up here"
            />
          )
          : (
            <ApprovedTimesheets
              timesheets={mappedApprovedTimesheets}
              totalTimesheetsCount={totalApprovedTimesheetsCount}
              getChecked={id => selectedTimesheetIdsToDownload.includes(id)}
              onExportClick={() => trackEvent(EXPORT_TIMESHEETS_BUTTON_CLICK)}
              onTimesheetRowClick={handleTimesheetRowClick}
              onTimesheetSelectChange={handleSelectedTimesheetsToDownloadChange}
              mobileUser={mobileUser}
              onBottomReached={fetchMoreApprovedTimesheets}
              isEndReached={!hasMoreApprovedTimesheets}
              scrollHeight={80}
              onTimesheetSelectAll={handleApprovedTimesheetSelectAll}
              areAllTimesheetsSelected={selectedTimesheetIdsToDownload.length > 0 && isEqual(selectedTimesheetIdsToDownload, orderedApprovedTimesheetIds)}
              downloadReady={downloadReady}
              timesheetsToDownload={mapTimesheetsToDownload(approvedTimesheets, selectedTimesheetIdsToDownload)}
              selectedTimesheetsCount={selectedTimesheetIdsToDownload.length}
            />
          )
        }
      </Loader>
    );
  };

  const renderRejectedTimesheets = () => {
    const submittedTimesheets = orderedTimesheets(timesheetsAwaitingApproval, orderedTimesheetsAwaitingApprovalIds);
    const rejectedTimesheets = submittedTimesheets.filter(timesheet => timesheet.rejectionState === 'REJECTED');

    return (
      <Loader loading={approvedTimesheetsLoading && !rejectedTimesheets.length} text="Loading...">
        { rejectedTimesheets.length === 0
          ? (
            <EmptyTimesheets
              title="No approved timesheets"
              description="Weployees&apos; approved timesheets will show up here"
            />
          )
          : (
            <RejectedTimesheets
              timesheets={rejectedTimesheets}
              totalTimesheetsCount={totalApprovedTimesheetsCount}
              getChecked={id => selectedTimesheetIdsToDownload.includes(id)}
              onExportClick={() => trackEvent(EXPORT_TIMESHEETS_BUTTON_CLICK)}
              onTimesheetRowClick={handleTimesheetRowClick}
              onTimesheetSelectChange={handleSelectedTimesheetsToDownloadChange}
              mobileUser={mobileUser}
              onBottomReached={fetchMoreApprovedTimesheets}
              isEndReached={!hasMoreApprovedTimesheets}
              scrollHeight={80}
              onTimesheetSelectAll={handleApprovedTimesheetSelectAll}
              areAllTimesheetsSelected={selectedTimesheetIdsToDownload.length > 0 && isEqual(selectedTimesheetIdsToDownload, orderedApprovedTimesheetIds)}
              downloadReady={downloadReady}
              timesheetsToDownload={mapTimesheetsToDownload(approvedTimesheets, selectedTimesheetIdsToDownload)}
              selectedTimesheetsCount={selectedTimesheetIdsToDownload.length}
            />
          )
        }
      </Loader>
    );
  };

  const renderTabContent = (tab) => {
    switch (tab) {
      case SUBMITTED_TIMESHEETS_TAB:
        return renderSubmittedTimesheets();
      case APPROVED_TIMESHEETS_TAB:
        return renderApprovedTimesheets();
      case REJECTED_TIMESHEETS_TAB:
        return renderRejectedTimesheets();
      default:
        return renderSubmittedTimesheets();
    }
  };

  const renderTabs = () => {
    if (FEATURE_FLAG_REJECTING_TIMESHEET) {
      return (
        TAB_NAMES.map(tabName => (
          <Tab label={tabName} />
        ))
      );
    }

    return (
      TAB_NAMES.slice(0, 2).map(tabName => (
        <Tab label={tabName} />
      ))
    );
  };

  const renderPage = () => {
    switch (currentPage) {
      default:
        return (
          <>
            {
              openTimesheet &&
              <TimesheetSummaryModal
                timesheet={openTimesheet}
                open={modalOpen}
                mobileUser={mobileUser}
                onClose={() => {
                  history.push(getInternalPath({ pathName: TIMESHEETS_NO_PARAMS_PATH }));
                  setModalOpen(false);
                }}
                onEmailClick={() => trackEvent(TIMESHEETS_QUERY_EMAIL_CLICK)}
                setModalOpen={setModalOpen}
                onRejectingTimesheet={handleRejectingTimesheet}
                weployerId={weployerDetails.userId}
                canRejectFeatureFlag={FEATURE_FLAG_REJECTING_TIMESHEET}
              />
            }
            <Snackbar
              visible={refetchingFromFilter}
              message="Loading timesheets..."
            />
            <StyledLargeWrapper>
              <h5>Timesheets</h5>
              <StyledSubtitle>Easily gain control of your Weployees&apos; timesheets with approvals</StyledSubtitle>
              <Grid container justify="space-between">
                <Grid item>
                  <Tabs value={tabIndex} onChange={handleTabClick}>
                    { renderTabs() }
                  </Tabs>
                </Grid>
                {
                  actions.includes(APPROVE_ORGANISATION_TIMESHEET) &&
                  <StyledOwnedByFilter>
                    <Grid item>
                      <Select
                        value={OWNED_BY_FILTERS[ownedByFilter]}
                        type={Select.TEXT}
                        onChange={handleOwnedByChange}
                        name="ownedBy"
                        id="owned-by"
                      >
                        <Option value={ME}>{OWNED_BY_FILTERS[ME]}</Option>
                        <Option value={ORGANISATION}>{OWNED_BY_FILTERS[ORGANISATION]}</Option>
                      </Select>
                    </Grid>
                  </StyledOwnedByFilter>
                }
              </Grid>
              {renderTabContent(TAB_NAMES[tabIndex])}
            </StyledLargeWrapper>
          </>
        );
      case PAYMENT_SUMMARY_PAGE:
        return (
          <StyledMediumWrapper>
            <StyledPaymentSummaryHeader>
              <StyledTitle>
                <LeftArrowIconButton
                  onClick={() => {
                    trackEvent(TIMESHEETS_BACK_BUTTON_CLICK);
                    setCurrentPage(SELECTION_PAGE);
                  }}
                />
                <StyledHeader>Confirm timesheets and payment</StyledHeader>
              </StyledTitle>
              <StyledSubHeading mobile={mobileUser}>
                Invoice details are grouped by role for varying hourly rates
              </StyledSubHeading>
            </StyledPaymentSummaryHeader>
            <PaymentSummary
              onBillingDetailsClick={() => trackEvent(TIMESHEETS_BILLING_DETAILS_CLICK)}
              onConfirmButtonClick={handleConfirmButtonClick}
              onCouponSelect={handleCouponSelect}
              onEmailClick={() => trackEvent(TIMESHEETS_QUERY_EMAIL_CLICK)}
              onPaymentTermSelect={handlePaymentTermSelect}
              onSkillInvoiceExpansion={() => trackEvent(TIMESHEETS_SKILL_INVOICE_OPEN)}
              selectedCoupon={selectedCoupon}
              selectedPaymentTerm={selectedPaymentTerm}
              setPaymentMethods={setPaymentMethods}
              timesheetIds={selectedTimesheetIds}
            />
          </StyledMediumWrapper>
        );
      case APPROVAL_SUCCESS_PAGE:
        return (
          <Loader loading={approvalSuccessLoading} text="These timesheets are being approved, please hold on...">
            <StyledSmallWrapper>
              <ApprovalSuccess
                isInvoice={paymentMethods.includes('Invoice')}
                isCreditCard={paymentMethods.includes('Credit card')}
                onButtonClick={() => trackEvent(TIMESHEETS_BACK_TO_TIMESHEETS_BUTTON_CLICK)}
              />
            </StyledSmallWrapper>
          </Loader>
        );
    }
  };

  return renderPage();
};

Timesheets.propTypes = {
  weployerDetails: PropTypes.object,
  onWeployerDetailsChange: PropTypes.func,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

Timesheets.defaultProps = {
  weployerDetails: {},
  onWeployerDetailsChange: () => {},
};

export default Timesheets;
